// One entity's change history in a dialog (wireframe components/Istoric.tsx),
// opened from a row of an admin list. The full log with filters and paging
// lives on the history page; this is the "what happened to this one" answer.
import { useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudModal, MudInfoBox, MudTable } from '@egov-moldova/mud-react';
import { EmptyRegisterMessage, formatDate, formatTime, onModalDismiss } from '@simsm/ui-shared';
import { apiErrorKey, loadFailureKind } from '../../app/api-client';
import { fetchHistory, type HistoryEntityKind } from './api';
import { catalogAttribution, HistoryActor } from './CatalogHistoryLog';
import { HistoryChanges } from './HistoryChanges';
import { HistoryMeta } from './HistoryMeta';

const RECENT_LIMIT = 25;

interface HistoryModalProps {
  open: boolean;
  onClose: () => void;
  kind: HistoryEntityKind;
  entityId: string | null;
  entityLabel: string;
}

export function HistoryModal({
  open,
  onClose,
  kind,
  entityId,
  entityLabel,
}: HistoryModalProps): JSX.Element {
  const { t } = useTranslate();

  const history = useQuery({
    queryKey: ['admin-history-entity', kind, entityId],
    queryFn: () => fetchHistory({ kinds: [kind], entityId: entityId ?? '', limit: RECENT_LIMIT }),
    enabled: open && entityId !== null,
  });

  const entries = history.data?.entries ?? [];

  return (
    <MudModal
      closeLabel={t('common.close')}
      open={open}
      size="lg"
      titleText={t('history.modal.title', { entity: entityLabel })}
      // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
      // components emit `mudClose` bubbling+composed, so a descendant closing inside this
      // modal reaches this handler too; the guard answers only a real dismissal.
      onMudClose={onModalDismiss(onClose)}
    >
      {/* A 404 is an answer about the entity, not a failed read: a Registrar
          Administrator naming a site that is not its Registrar's is told it was
          not found (SITE_NOT_FOUND, docs/shape-uc-30-sites.md §10). */}
      {history.isError ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {loadFailureKind(history.error) === 'not_found'
            ? t(apiErrorKey(history.error, 'form.error.not_found'))
            : t('common.load_error')}
        </MudInfoBox>
      ) : null}
      {history.isLoading ? (
        <p role="status" aria-live="polite">
          {t('common.loading')}
        </p>
      ) : null}
      {/* A failed read printed the error banner and "no history yet" together,
          and the second sentence is a claim about the entity the first one says
          we could not check. An empty log still shows its table, which says in
          one spanning cell that nothing was recorded (IstoricLocalitati.tsx:54). */}
      {!history.isLoading && !history.isError ? (
        <div className="simsm-history__table">
          <MudTable
            className="simsm-table"
            columns={[
              // The two side columns hold two short lines each — a date over an
              // hour, a name over a source — while the diff in the middle holds
              // a field label beside a `before → after` phrase. Left at 10rem
              // apiece they took 320 of the dialog's 720px and the diff wrapped
              // `municipiu` as `municipi/u`.
              { key: 'recordedAt', label: t('history.column.when'), width: '8rem' },
              { key: 'change', label: t('history.column.change') },
              { key: 'actor', label: t('history.column.actor'), width: '9rem' },
            ]}
            // `change` and `actor` are slotted below: the diff and the three
            // attribution states need markup a cell string cannot hold.
            // `recordedAt` is slotted below, like `change` and `actor`: the day
            // and the hour go on two lines, which a cell string cannot hold.
            rows={entries.map((entry) => ({ id: entry.id }))}
            rowStyle="divided"
            aria-label={t('history.modal.title', { entity: entityLabel })}
          >
            <EmptyRegisterMessage>{t('history.modal.empty')}</EmptyRegisterMessage>
            {/* The day, then the hour beneath it — the wireframe's own two lines
                (`IstoricLocalitati.tsx`: `<div>{fmtDate(e.d)}</div>` over a
                MetadataLine carrying `Ora`), and what `CatalogHistoryLog` already
                renders one route over. This modal printed both on one line
                through `formatDateTime`, so the same event read two ways
                depending on which surface it was opened from. */}
            {entries.map((entry, index) => (
              <span
                key={`recorded-${entry.id}`}
                slot={`cell-recordedAt-${index}`}
                className="simsm-history__cell"
              >
                <span>{formatDate(entry.recordedAtIso)}</span>
                <HistoryMeta
                  label={t('history.meta.label.time')}
                  value={formatTime(entry.recordedAtIso)}
                />
              </span>
            ))}
            {entries.map((entry, index) => (
              <span
                key={`change-${entry.id}`}
                slot={`cell-change-${index}`}
                className="simsm-history__cell"
              >
                <HistoryChanges entry={entry} />
              </span>
            ))}
            {entries.map((entry, index) => (
              <span
                key={`actor-${entry.id}`}
                slot={`cell-actor-${index}`}
                className="simsm-history__cell"
              >
                <HistoryActor entry={catalogAttribution(entry)} />
              </span>
            ))}
          </MudTable>
        </div>
      ) : null}
      <div slot="actions">
        <MudButton shape="circular" variant="neutral" size="md" onClick={onClose}>
          {t('common.close')}
        </MudButton>
      </div>
    </MudModal>
  );
}
